import { useMemo, useRef } from 'react';

import { useAbTestCheckoutWithoutUpselling } from '@hooks';
import { IEcommerceServices } from '@model';
import { ECartType, EcommerceSectionNamespace, trackBeginCheckout } from '@services/ecommerceService';
import { AdyenV6 } from '@services/paymentService';
import { useRedirect } from '@services/routingService';
import { nonAwaitedPromiseHandlerParametrized } from '@utils/async';

import { useCartPageContext, ECartPageState } from '../../../context';
import { createEcommerceCheckout, createEcommerceOaltCheckout } from '../../../ecommerce';
import { useValidateLastOrderCart } from '../../../services';

/**
 * Custom hook that setups all necessary actions for checkout process
 * @returns {{ canProceed: boolean, proceed: () => Promise<void> }}
 * - canProceed - defines whether the user can proceed into checkout process
 * - proceed - callback that will handle all necessary actions and initiates checkout process
 */
export const useBeginCheckoutProcess = (): {
  canProceed: boolean;
  proceed: (interaction?: EcommerceSectionNamespace.EEcommerceInteraction) => void;
} => {
  // Temp solution
  const shouldOpenOalt = useRef(true);

  const { redirectPage } = useAbTestCheckoutWithoutUpselling();
  const redirect = useRedirect();

  const {
    settings: {
      orderAsLastTimeEnabled,
      adyen: { clientKey },
    },
    state: { lastOrder, proceedCheckoutEnabled, products, pricesSummary, gifts, serviceItems },
    actions: { setCartPageState, setLastOrder, validateCart },
    settings,
  } = useCartPageContext();

  const { request: validateLastOrderCart } = useValidateLastOrderCart();

  const isPaymentMethodAvailable = AdyenV6.usePaymentAvailability(clientKey, settings.adyen);

  return useMemo(() => {
    const proceed = async (
      eventInteraction: EcommerceSectionNamespace.EEcommerceInteraction = EcommerceSectionNamespace
        .EEcommerceInteraction.BUTTON_CHECKOUT,
    ) => {
      await validateCart();

      const getEcommerceServices = (): IEcommerceServices => ({
        settings: settings,
        gifts: gifts.some((x) => !x.hideInCart),
      });

      if (orderAsLastTimeEnabled && lastOrder) {
        const payload = { ...lastOrder };
        delete payload.freeDeliveryOptionFlag;
        delete payload.paymentInfo.paypalTokenAvailable;

        const response = await validateLastOrderCart(payload);

        if (response?.valid && isPaymentMethodAvailable(lastOrder.paymentInfo.providerType) && shouldOpenOalt.current) {
          shouldOpenOalt.current = false;

          setLastOrder({
            ...lastOrder,
            paymentInfo: {
              ...lastOrder.paymentInfo,
              paypalTokenAvailable: response.paymentInfo?.paypalTokenAvailable,
            },
            orderPricesSummary: response.prices,
            freeDeliveryOptionFlag: response.freeDeliveryOptionFlag,
          });
          setCartPageState(ECartPageState.ORDER_AS_LAST_TIME);

          const ecommerceCheckout = createEcommerceOaltCheckout(
            lastOrder,
            products,
            pricesSummary,
            serviceItems,
            getEcommerceServices(),
          );
          trackBeginCheckout(ecommerceCheckout, ECartType.Cart, eventInteraction);

          return;
        }
      }

      trackBeginCheckout(
        createEcommerceCheckout(products, pricesSummary, serviceItems, getEcommerceServices()),
        ECartType.Cart,
        eventInteraction,
      );
      redirect(redirectPage);
    };

    return {
      canProceed: proceedCheckoutEnabled,
      proceed: (interaction?: EcommerceSectionNamespace.EEcommerceInteraction) =>
        nonAwaitedPromiseHandlerParametrized(proceed, interaction),
    };
  }, [
    proceedCheckoutEnabled,
    validateCart,
    orderAsLastTimeEnabled,
    lastOrder,
    settings,
    gifts,
    products,
    pricesSummary,
    serviceItems,
    redirect,
    redirectPage,
    validateLastOrderCart,
    isPaymentMethodAvailable,
    setLastOrder,
    setCartPageState,
  ]);
};
